import React, { useState } from 'react';
import {
  Icon,
  Modal,
  PrimaryButton,
  Spinner,
  Typography,
} from 'glints-aries/es/@next';
import { Blue, Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space12, space16 } from 'glints-aries/es/@next/utilities/spacing';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { usePollOrderStatus } from '../../../PaymentOrder/hooks/usePollOrderStatus';
import {
  PaymentOrderStatus,
  PaymentOrderType,
  VirtualProductOrderStatusResp,
} from '../../../PaymentOrder/types';
import { mapVirtualProductOrderStatusResp } from '../../CreditPurchaseConfirmation/mappers/restToGraphQL';
import { CreditProductPurchaseOrderStatus } from '../../types';
import useRefetchAfterCreditPurchase from '../PurchaseProductOrderSummarySection/hooks/useRefetchAfterCreditPurchase';
import { CreditProductPurchaseOrderForAfterPayment } from '../types';

type Props = {
  orderNumber: string;
  jobId?: string;
  onComplete: (order: CreditProductPurchaseOrderForAfterPayment) => void;
  onClose: () => void;
};

export default function V2OrderProcessingModal({
  orderNumber,
  jobId,
  onComplete,
  onClose,
}: Props) {
  const [hasError, setHasError] = useState(false);
  const refetchAfterCreditPurchase = useRefetchAfterCreditPurchase();

  usePollOrderStatus<VirtualProductOrderStatusResp>({
    orderNumber,
    orderType: PaymentOrderType.VIRTUAL_PRODUCT,
    onCompleted: data => {
      const mapped = mapVirtualProductOrderStatusResp(data);
      const completedOrder = mapped.creditProductPurchaseOrder;

      if (
        !completedOrder ||
        completedOrder.status !== CreditProductPurchaseOrderStatus.SUCCESS ||
        data.orderStatus !== PaymentOrderStatus.COMPLETED
      ) {
        setHasError(true);
        return;
      }

      refetchAfterCreditPurchase({
        productType: completedOrder.product.type,
        jobId:
          completedOrder.afterPurchaseActionLog?.relatedJobId ||
          jobId ||
          undefined,
        status: completedOrder.status,
      });
      onComplete(completedOrder);
      onClose();
    },
    onError: () => {
      setHasError(true);
    },
  });

  let content = <LoadingState />;
  if (hasError) {
    content = <ErrorState onClose={onClose} />;
  }

  return (
    <StyledModal isOpen={true} onClose={onClose} closeOnClickOutside={false}>
      <Content>{content}</Content>
    </StyledModal>
  );
}

function LoadingState() {
  return (
    <StateWrapper>
      <Spinner fill={Blue.S99} size="large" />
      <Typography variant="body1" color={Neutral.B18}>
        <FormattedMessage
          id="text-finalizing-your-purchase"
          defaultMessage="Finalizing your purchase..."
        />
      </Typography>
    </StateWrapper>
  );
}

function ErrorState({ onClose }: { onClose: () => void }) {
  return (
    <StateWrapper>
      <PendingIcon
        name="ri-error-warning-line"
        width={48}
        height={48}
        fill={Blue.S99}
      />
      <Typography variant="subtitle1" color={Neutral.B18}>
        <FormattedMessage
          id="text-something-went-wrong"
          defaultMessage="Something went wrong"
        />
      </Typography>
      <Description variant="body2" color={Neutral.B40}>
        <FormattedMessage
          id="text-processing-order-error-description"
          defaultMessage="We couldn't finalize your purchase. Please try again or contact support."
        />
      </Description>
      <PrimaryButton onClick={onClose}>
        <FormattedMessage id="interactive-close" defaultMessage="Close" />
      </PrimaryButton>
    </StateWrapper>
  );
}

const StyledModal = styled(Modal)`
  .modal-content {
    min-width: 480px;
    max-width: 480px;
    padding: ${space16};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
`;

const StateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${space12};
  width: 100%;
`;

const PendingIcon = styled(Icon)`
  margin-bottom: ${space12};
`;

const Description = styled(Typography)`
  max-width: 360px;
`;
